import { INVALID_CHART_RECID, isValidChartRecid } from '@/composables/isValidChartRecid';
import { extractStyleCacheId } from '@/utils/extractStyleCacheId';
import { getStyleCacheSettingsOverrides } from '@/utils/getStyleCacheSettingsOverrides';

const SYMBOL_URL_PARAMETER_QUERY_STRING_KEY = 's';
const STYLE_CACHE_ID_URL_PARAMETER_QUERY_STRING_KEY = 'i';
const STYLE_CACHE_ID_ALTERNATE_URL_PARAMETER_QUERY_STRING_KEY = 'id';
const CHART_RECID_URL_PARAMETER_QUERY_STRING_KEY = 'a';
const UI_BELOW_CHART_URL_PARAMETER_QUERY_STRING_KEY = 'uiBelowChart';
const FORCE_URL_PARAMETER_QUERY_STRING_KEY = 'force';

function getSymbolUrlParameterValue(urlQueryString?: string) {
  const urlSearchParams = new URLSearchParams(urlQueryString ?? window.location.search);
  const symbol = urlSearchParams.get(SYMBOL_URL_PARAMETER_QUERY_STRING_KEY);

  if (typeof symbol !== 'string') {
    return '';
  }

  const symbols = symbol
    .replace(/\s/g, '')
    .toUpperCase()
    .split(',')
    .filter(s => !!s);

  if (!symbols.length) {
    return '';
  }

  return symbols[0];
}

function getStyleCacheIdUrlParameterValue(urlQueryString?: string) {
  const urlSearchParams = new URLSearchParams(urlQueryString ?? window.location.search);
  const rawIdCode = urlSearchParams.get(STYLE_CACHE_ID_ALTERNATE_URL_PARAMETER_QUERY_STRING_KEY) ?? '';
  const formattedIdCode = extractStyleCacheId(rawIdCode);

  if (formattedIdCode)
    return formattedIdCode;

  const rawICode = urlSearchParams.get(STYLE_CACHE_ID_URL_PARAMETER_QUERY_STRING_KEY) ?? '';
  const formattedICode = extractStyleCacheId(rawICode);

  return formattedICode;
}

function getChartRecidUrlParameterValue(urlQueryString?: string) {
  const urlSearchParams = new URLSearchParams(urlQueryString ?? window.location.search);
  const recidParameter = urlSearchParams.get(CHART_RECID_URL_PARAMETER_QUERY_STRING_KEY);

  if (typeof recidParameter !== 'string')
    return INVALID_CHART_RECID;

  const chartRecid = recidParameter.replace(/\s/g, '');

  return isValidChartRecid(chartRecid) ? Number(chartRecid) : INVALID_CHART_RECID;
}

function getStyleCacheSettingsOverridesUrlParameterValues(urlQueryString?: string) {
  return getStyleCacheSettingsOverrides(urlQueryString ?? window.location.search);
}

function getUIBelowChartUrlParameterValue(urlQueryString?: string) {
  const urlSearchParams = new URLSearchParams(urlQueryString ?? window.location.search);

  return urlSearchParams.get(UI_BELOW_CHART_URL_PARAMETER_QUERY_STRING_KEY);
}

function removeAllNonChartRelatedURLParameters(urlSearchParams: URLSearchParams) {
  urlSearchParams.delete(UI_BELOW_CHART_URL_PARAMETER_QUERY_STRING_KEY);
  urlSearchParams.delete(FORCE_URL_PARAMETER_QUERY_STRING_KEY);
}

export function useUrlParameters() {
  return {
    getSymbolUrlParameterValue,
    getStyleCacheIdUrlParameterValue,
    getChartRecidUrlParameterValue,
    getStyleCacheSettingsOverridesUrlParameterValues,
    getUIBelowChartUrlParameterValue,
    removeAllNonChartRelatedURLParameters
  };
}
